import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CheckBox,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Spinner,
    Text,
    TextInput,
} from 'grommet';
import { useLoaderData } from 'react-router-dom';
import {
    API,
    ContactNotesModal,
    FilterOptionSelect,
    RowsPerPageSelect,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';
import { AddToListButton } from './AddToListButton';
import { contactListColumns, fullName } from './contactListColumns';
import { loadListContacts, loadMajors } from './contactListQueries';

const SEARCH_DEBOUNCE_MS = 400;

export const ContactList = () => {
    const list = useLoaderData();
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selection, setSelection] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [hasNotesOnly, setHasNotesOnly] = useState(false);
    const [majorOptions, setMajorOptions] = useState([]);
    const [selectedMajors, setSelectedMajors] = useState([]);
    const [notesContact, setNotesContact] = useState(null);
    // Bumped after a remove / add so the effect refetches the current page.
    const [refreshKey, setRefreshKey] = useState(0);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'lastName', direction: 'asc' },
    });

    const refresh = () => setRefreshKey((key) => key + 1);

    // The input stays instant; only the debounced value drives a refetch.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const majors = await loadMajors();
            if (isActive) setMajorOptions(majors);
        })();

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        if (!list?.id) return;
        let isActive = true;

        (async () => {
            const result = await loadListContacts({
                listId: list.id,
                page,
                pageSize,
                sort,
                search: appliedSearchTerm,
                hasNotesOnly,
                majorIds: selectedMajors.map((major) => major.id),
            });
            if (!isActive) return;

            // Selection is per page, so new rows always start unchecked.
            // A failed load shows no rows rather than the previous query's.
            setSelection([]);
            setIsLoading(false);
            setContacts(result?.contacts ?? []);
            if (result) applyPagination(result.meta);
        })();

        return () => {
            isActive = false;
        };
    }, [
        list?.id,
        page,
        pageSize,
        sort,
        appliedSearchTerm,
        hasNotesOnly,
        selectedMajors,
        refreshKey,
        applyPagination,
    ]);

    const removeFromList = async () => {
        if (selection.length === 0) return;

        try {
            await API.put(`/api/contact-lists/${list.id}`, {
                data: { contacts: { disconnect: selection } },
            });
            refresh();
        } catch (error) {
            console.log(error);
        }
    };

    const renderTable = () => {
        if (isLoading) return <Spinner alignSelf="center" />;
        if (contacts.length === 0) {
            return <Text alignSelf="center">No students found</Text>;
        }
        return (
            <DataTable
                columns={contactListColumns({ onViewNotes: setNotesContact })}
                data={contacts}
                primaryKey="id"
                sort={{ ...sort, external: true }}
                onSort={handleSort}
                allowSelectAll
                select={selection}
                onSelect={setSelection}
            />
        );
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Heading level="2">{list?.attributes?.name}</Heading>
                <Box direction="row" gap="small">
                    <Button
                        secondary
                        size="large"
                        label="Remove checked from list"
                        onClick={removeFromList}
                    />
                    <AddToListButton listId={list?.id} revalidate={refresh} />
                </Box>
                <Box direction="row" align="center" gap="medium" wrap>
                    <Box width="medium">
                        <TextInput
                            a11yTitle="Search students"
                            placeholder="Search by name, email, phone, or location"
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                applySearchTerm(event.target.value);
                            }}
                        />
                    </Box>
                    <Box width="medium">
                        <FilterOptionSelect
                            placeholder="Majors"
                            options={majorOptions}
                            labelKey="name"
                            valueKey="id"
                            value={selectedMajors}
                            updateFormData={(majors) => {
                                setSelectedMajors(majors);
                                setPage(1);
                            }}
                        />
                    </Box>
                    <CheckBox
                        label="Has Notes"
                        checked={hasNotesOnly}
                        onChange={({ target }) => {
                            setHasNotesOnly(target.checked);
                            setPage(1);
                        }}
                        toggle
                    />
                </Box>
                <Box pad={{ vertical: 'small' }}>{renderTable()}</Box>
                <Box
                    direction="row-responsive"
                    align="center"
                    justify="between"
                    gap="small"
                    pad="small">
                    <RowsPerPageSelect
                        options={stepOptions}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    />
                    <Pagination
                        numberItems={totalItems}
                        page={page}
                        onChange={handlePageChange}
                        step={pageSize}
                        summary={true}
                    />
                </Box>
                {notesContact && (
                    <ContactNotesModal
                        contactId={notesContact.id}
                        contactName={fullName(notesContact)}
                        onClose={() => setNotesContact(null)}
                    />
                )}
            </PageContent>
        </Page>
    );
};

export async function loader({ params }) {
    const query = QueryString.stringify(
        { fields: ['name'] },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(
            `/api/contact-lists/${params.listId}?${query}`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}
